import { FormGroup } from "@angular/forms";
import { PaginationParams } from "../interfaces/PaginationParams";
import { HttpParams } from "@angular/common/http";
import { QueryParam } from "../interfaces/queryParams";
import { TableLazyLoadEvent } from "primeng/table";
import { FilterMatchMode, FilterMetadata } from "primeng/api";

export class Utils {

  static markAllFormControlsAsTouched(form: FormGroup<any>) {
    Object.keys(form.controls).forEach(field => {
      const control = form.get(field);
      if (control instanceof FormGroup) {
        // Recursively mark child controls as touched and dirty
        Utils.markAllFormControlsAsTouched(control);
      } else {
        control?.markAsTouched({ onlySelf: true });
        control?.markAsDirty({ onlySelf: true });
      }
    });
  }

  static createPaginationParams(params?: PaginationParams): HttpParams {
    let httpParams = new HttpParams();
    if (params?.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params?.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }
    return httpParams;
  }

  static getTramiteEstadoTagIcon(estado: string, usuarioAsignadoCorreo: string): string {
    if (estado === 'REABIERTO') {
      return 'pi pi-flag';
    } else if (estado === 'REASIGNADO') {
      return 'pi pi-flag-fill';
    } else if (estado === 'FINALIZADO') {
      return 'pi pi-star-fill';
    } else if (estado === 'PENDIENTE' && usuarioAsignadoCorreo !== null) {
      return 'pi pi-star';
    } else {
      return 'pi pi-clock';
    }
  }

  static getTramiteEstadoTagSeverity(estado: string, usuarioAsignadoCorreo: string): 'success' | 'secondary' | 'info' | 'warning' | 'danger' | 'contrast' {
    if (estado === 'REABIERTO' || estado === 'REASIGNADO' || estado === 'FINALIZADO') {
      return 'success';
    } else if (estado === 'PENDIENTE' && usuarioAsignadoCorreo !== null) {
      return 'warning';
    } else {
      return 'danger';
    }
  }
  /**
   * @description Compara dos objetos y verifica si son iguales
   * @param obj1
   * @param obj2
   * @returns
   */
  static isDeepEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) return true;

    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key) || !this.isDeepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  }

  /**
   * @description Devuelve una URI codificada con query params
   * @param url La dirección principal sin '?'
   * @param params Array de un objeto <b>QueryParam</b>
   * @see QueryParam
   * @author Andrés Mtz
   */
  public static readonly createQueryParams = (url: string, params: QueryParam[]) => {
    let newUrl = `${url}?`;
    params.forEach((f, i) => {
      if (i == 0)
        newUrl += `${f.key}=${f.value}`;
      else
        newUrl += `&${f.key}=${f.value}`
    });
    return encodeURI(newUrl);
  }

  public static readonly createQueryParamsFilter = (url: string, params: QueryParam[]) => {
    let newUrl = `${url}`;
    params.forEach((f, i) => {
      if (f.value) {
        if (i === 0) {
          newUrl += `?${f.key}=${f.value}`;
        } else {
          newUrl += `&${f.key}=${f.value}`;
        }
      }
    });
    return encodeURI(newUrl);
  }

  /**
   * @description Crea un FormData con body params
   * @param params Array de un objeto <b>QueryParam</b>
   * @see QueryParam
   * @author Andrés Mtz
   */
  public static readonly createFormData = (params: QueryParam[]) => {
    const formData = new FormData();
    params.forEach(param => {
      formData.append(param.key, param.value);
    });
    return formData;
  }

  static isMobile(): boolean {
    return window.innerWidth <= 700;
  }

  /**
   * @description Obtiene los parámetros de paginación para la tabla, obtiene los filtros (por ejemplo nombre, apellido, etc),
   * ordenamiento y paginación
   * @param event El objeto que devuelve el evento onLazyLoad del table de PrimeNG
   * @returns HttpParams
   */
  static getLazyLoadParams(event: TableLazyLoadEvent): HttpParams {
    let params = new HttpParams()
    if (!event) {
      return params;
    }
    if (event.first && event.rows) {
      const requestPage = event.first / event.rows;
      const nextPage = requestPage > 0 ? requestPage : 0;
      params = params.set('page', nextPage);
      params = params.set('size', event.rows);
    }
    if (event.sortField && event.sortOrder) {
      params = params.set('sort', event.sortField + ',' + (event.sortOrder == 1 ? 'asc' : 'desc'));
    }
    for (let name in event?.filters) {
      const filterValue = event.filters[name];
      const filter: FilterMetadata | undefined = Array.isArray(filterValue) ? filterValue[0] : filterValue;
      if (filter?.value) {
        switch (filter.matchMode) {
          case FilterMatchMode.CONTAINS:
            params = params.set(name, `%${filter.value}%`);
            break;
          case FilterMatchMode.STARTS_WITH:
            params = params.set(name, `${filter.value}%`);
            break;
          case FilterMatchMode.ENDS_WITH:
            params = params.set(name, `%${filter.value}`);
            break;
          default:
            params = params.set(name, filter.value);
            break;
        }
      }
    }
    return params;
  }

  public static castDateToString(date: Date): string {
    if (!date) throw 'Invalid date';
    return date.toISOString().substring(0, date.toISOString().indexOf('T'));
  }
}
